import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { plans } from  './constants';
import * as Sentry from "@sentry/react";
import { captureError, logRequest, finishRequest } from './sentry';

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export const generateLinkToWebsite = path => `${import.meta.env.VITE_SITE_URL}${path}`;

export const imageUrl = imageName => `${import.meta.env.VITE_ASSETS_URL}${imageName}`;

export function getUrlParams() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams;
}

export function getPlan() {
  const urlParams = getUrlParams();
  const planId = urlParams.get('planId') || null;
  const planYear = urlParams.get('planYear') || null;
  let plan = null;
  if (planId && planYear) {
    const serchPlan = plans.filter(item => item.id === planId && item.year === planYear);
    if (serchPlan.length > 0) {
      plan = serchPlan[0];
    }
  }
  return plan;
}

export function getUrlProviderType() {
  const urlParams = getUrlParams();
  const providerType = urlParams.get('providerType') || '0';
 
  return providerType;
}

export function isEnrollmentActive(date = new Date()) {
  if (typeof window !== 'undefined' && typeof window.enrollmentActive === 'boolean') {
    return window.enrollmentActive;
  }

  const monthDay = (date.getMonth() + 1) * 100 + date.getDate();
  return monthDay >= 1015 && monthDay <= 1231;
}

export function calculateMapCenterFromMarkers(markers) {

  var lat = 27.903927, lng = -81.452336;
  var zoom = 15;
  if (markers?.length > 0) {
    lat = markers[0].latitude; lng = markers[0].longitude;

    if (markers.length > 1) {
      var latArray = [];
      var lngArray = []; 

      for (var i = 0; i < markers.length; i++) {
          latArray.push(markers[i].latitude);
          lngArray.push(markers[i].longitude);
      }

      var minlat = Math.min.apply(Math, latArray),
          minlng = Math.min.apply(Math, lngArray),
          maxlat = Math.max.apply(Math, latArray),
          maxlng = Math.max.apply(Math, lngArray);

      lat = (minlat + ((maxlat - minlat) / 2));
      lng = (minlng + ((maxlng - minlng) / 2));

      var GLOBE_WIDTH = 256;
      var west = minlng;
      var east = maxlng;

      var south = minlat;
      var nort = maxlat;

      var angle = east - west;
      if (angle < 0) {
          angle += 360;
      }

      var ewZoom = Math.round(Math.log(GLOBE_WIDTH * 360 / angle / GLOBE_WIDTH) / Math.LN2);

      angle = nort - south;
      if (angle < 0) {
          angle += 360;
      }

      var snZoom = Math.round(Math.log(GLOBE_WIDTH * 360 / angle / GLOBE_WIDTH) / Math.LN2);

      zoom = ewZoom;
      if (snZoom < ewZoom)
          zoom = snZoom;

      if (isNaN(zoom) || zoom > 17)
          zoom = 17;
    }
  }
  
  return { center: { lat, lng}, zoom };

}

export function formatPhoneNumber(number) {
  // Convertir el número a cadena para facilitar la manipulación
  const str = number.toString();

  // Validar que el número tenga 10 dígitos
  if (str.length !== 10) {
    throw new Error("El número debe tener exactamente 10 dígitos.");
  }

  // Formatear el número en el formato XXX-XXX-XXXX
  return `${str.slice(0, 3)}-${str.slice(3, 6)}-${str.slice(6)}`;
}

export function formatLanguages(languageString) {

  if (languageString === '' || languageString === null || languageString === undefined) return languageString;

  // Divide la cadena en pares separados por punto y coma
  const pairs = languageString.split(";");

  // Mapear cada par para obtener solo el nombre del idioma y devolverlo en un nuevo arreglo
  const languages = pairs.map(pair => {
    const [, language] = pair.split(",");
    return language;
  });

  // Unir los idiomas en una sola cadena separados por coma y espacio
  return languages.join(", ");
}

export function getCodeLanguagesList(languageString) {

  if (languageString === '' || languageString === null || languageString === undefined) return [];

  // Divide la cadena en pares separados por punto y coma
  const pairs = languageString.split(";");

  // Mapear cada par para obtener solo el nombre del idioma y devolverlo en un nuevo arreglo
  const languages = pairs.map(pair => {
    const [code, language] = pair.split(",");
    return language.replaceAll('"', '');
  });

  // Unir los idiomas en una sola cadena separados por coma y espacio
  return languages;
}

export async function getRequest(path, params = null) {
  let headers = new Headers();
  headers.append('Content-Type', 'application/json');
  headers.append('Accept', 'application/json');
  const options = {
    method: "GET",
    redirect: "follow",
    mode: "cors",
    headers,
  };
  let url = params ? `${path}?${new URLSearchParams(params)}` : path;
  const fullUrl = `${import.meta.env.VITE_API_URL}/${url}`;

  // Registrar la solicitud en Sentry
  const requestId = logRequest('GET', fullUrl, params || {});

  try {
    const response = await fetch(fullUrl, options);

    // Finalizar la transacción
    finishRequest(requestId, response.ok ? 'success' : 'error');

    if (!response.ok) {
      throw new Error(`Error en la solicitud GET: ${response.statusText}`);
    }

    try {
      const result = await response.json();
      return result;
    } catch (jsonError) {
      console.error("Error al analizar el JSON:", jsonError);
      throw new Error("No se pudo analizar la respuesta JSON.");
    }
  } catch (fetchError) {
    console.error("Error en la solicitud al servidor:", fetchError);
    // Capturar el error en Sentry
    captureError(fetchError, { url: fullUrl, params: params || {} });
    throw new Error("Error en la solicitud GET al servidor.");
  }
}


export async function postRequest(path, params = null, body = null) {
  let headers = new Headers();
  headers.append('Content-Type', 'application/json');
  headers.append('Accept', 'application/json');
  const options = {
    method: "POST",
    redirect: "follow",
    mode: "cors",
    headers,
  };
  if (body) {
    options.body = JSON.stringify(body);
  }
  let url = params ? `${path}?${new URLSearchParams(params)}` : path;
  const fullUrl = `${import.meta.env.VITE_API_URL}/${url}`;

  // Registrar la solicitud en Sentry
  const requestId = logRequest('POST', fullUrl, params || {}, body || {});

  try {
    const response = await fetch(fullUrl, options);

    // Finalizar la transacción
    finishRequest(requestId, response.ok ? 'success' : 'error');

    if (!response.ok) {
      throw new Error(`Error en la solicitud POST: ${response.statusText}`);
    }

    try {
      const result = await response.json();
      return result;
    } catch (jsonError) {
      throw new Error("No se pudo analizar la respuesta JSON.");
    }
  } catch (fetchError) {
    // Capturar el error en Sentry
    captureError(fetchError, { url: fullUrl, params: params || {}, body: body || {} });
    throw fetchError;
  }
}


export async function printRequest(path, params = null, body = {}) {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");

  const raw = JSON.stringify(body);
  const requestOptions = {
    method: "POST",
    headers,
    body: raw,
    redirect: "follow"
  };
  let url = params ? `${path}?${new URLSearchParams(params)}` : path; 
  const fullUrl = `${import.meta.env.VITE_API_URL}/${url}`;

  // Registrar la solicitud en Sentry
  const requestId = logRequest('PRINT', fullUrl, params || {}, body || {});

  try {
    const response = await fetch(fullUrl, requestOptions);

    // Finalizar la transacción
    finishRequest(requestId, response.ok ? 'success' : 'error');

    if (!response.ok) {
      throw new Error(`Error al descargar el PDF: ${response.statusText}`);
    }

    try {
      const blob = await response.blob(); // Obtener el contenido como Blob
      const downloadUrl = window.URL.createObjectURL(blob); // Crear URL para el Blob
      const a = document.createElement("a"); // Crear un elemento de anclaje (link)
      a.href = downloadUrl;
      a.download = "providers.pdf"; // Nombre para el archivo descargado
      document.body.appendChild(a); // Agregar el elemento al DOM
      a.click(); // Simular el click para iniciar la descarga
      a.remove(); // Eliminar el elemento después de la descarga
      window.URL.revokeObjectURL(downloadUrl); // Liberar memoria del Blob
    } catch (blobError) {
      console.error("Error al procesar el archivo PDF:", blobError);
      throw new Error("No se pudo procesar el archivo PDF.");
    }

  } catch (fetchError) {
    console.error("Error en la solicitud al servidor:", fetchError);
    // Capturar el error en Sentry
    captureError(fetchError, { url: fullUrl, params: params || {}, body: body || {} });
    throw new Error("Error en la solicitud al servidor.");
  }

  return;
}


